/**
 * Health Profile Summary data helpers.
 * Questions to include are identified by the `show_in_health_summary`
 * boolean column on recipe_questions (set directly in the DB), replacing
 * the old regex + answer-shape heuristics.
 */

import * as translator from "../../../utils/translator";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

/** Exclusive-none options are marked in DB via validation_rule is_exclusive_none. */
function isExclusiveNoneOption(node: any): boolean {
  return !!node?.recipe?.validation_rule?.is_exclusive_none;
}

/** Month/day-aware age calculation — mirrors api/src/managers/AnalyticsManager.ts's calculateAge. */
export function calculateAge(birthday: unknown): number | null {
  if (!birthday) return null;

  // Prefer local yyyy-mm-dd parse so "2024-12-24" is not shifted by UTC midnight.
  const raw = String(birthday).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const birthDate = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(raw);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/** First child whose answer is selected, or null. */
export function findSelectedChild(children: any[] | undefined, answersSelectedMap: any): any | null {
  return (children || []).find((child) => answersSelectedMap[child.recipe.id]) ?? null;
}

export interface HealthSummaryEntry {
  key: string | number;
  label: string;
  value: string;
  /** `id` of the flattened question the row came from (HL-12029: the pencil opens it). */
  questionId: number | string;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

const YES_NO_VALUES = new Set(["yes", "ja", "oui", "no", "nein", "non"]);

// Matches "none of the above" phrasing in German, English, and French.
const NONE_OF_THE_ABOVE_RE =
  /\bkein(?:e|es|er)?\s+(?:der\s+)?(?:genannten|obigen|davon)\b|\bnone\s+of\s+(?:the\s+)?(?:above|these|listed)\b|\baucun(?:e)?\s+(?:de(?:s|r)?\s+\S+\s+)?ci-dessus\b|\baucun(?:e)?\s+de\s+ces\b/i;

function normalizeNoneOfTheAbove(value: string | null | undefined, language: string | undefined): string | null {
  if (!value) return value ?? null;
  return NONE_OF_THE_ABOVE_RE.test(value) ? t(language, "health_summary_none_short") || value : value;
}

function isYesNoValue(value: unknown): boolean {
  return YES_NO_VALUES.has(String(value ?? "").trim().toLowerCase());
}

/**
 * First filled textarea/text under `node` (depth-first). Used so a Yes/No
 * gate that reveals a free-text follow-up (e.g. "other conditions") shows
 * the typed condition instead of just "Ja"/"Yes".
 */
function findFilledFreeText(node: any, answersSelectedMap: any): string | null {
  if (!node?.recipe) return null;
  const type = node.recipe.type;
  if ((type === "textarea" || type === "text") && answersSelectedMap[node.recipe.id]) {
    const text = String(answersSelectedMap[node.recipe.id]).trim();
    if (text) return text;
  }
  for (const child of node.children || []) {
    const found = findFilledFreeText(child, answersSelectedMap);
    if (found) return found;
  }
  return null;
}

const OPTION_TYPES = new Set(["radio_button", "select_option", "checkbox_option"]);

/**
 * Specific values under a selected Yes/No (or any) option: a typed follow-up,
 * or the deepest selected medication/condition — never the bare "Ja"/"Yes"
 * when something more specific exists (current-treatment select under Q999).
 * A Yes/No nested under an already-specific option asks about something else
 * (condition → "taking medication? Ja → …"), so its follow-up is shown next to
 * the option instead of replacing it: "Prä-Diabetes (Metformin)".
 */
function collectSpecificValues(answerNode: any, answersSelectedMap: any): string[] {
  const specificValues: string[] = [];
  // `parentFollowUpValues`: where values behind such a nested Yes/No go; null outside a specific option.
  const collectFromAnswer = (answer: any, parentFollowUpValues: string[] | null) => {
    if (!answer?.recipe) return;
    const answerType = answer.recipe.type;
    if ((answerType === "textarea" || answerType === "text") && answersSelectedMap[answer.recipe.id]) {
      const typedText = String(answersSelectedMap[answer.recipe.id]).trim();
      if (typedText) specificValues.push(typedText);
      return;
    }
    const isOption = OPTION_TYPES.has(answerType);
    const isSelectedOption = isOption && answersSelectedMap[answer.recipe.id];
    const subAnswers = answer.children || [];
    if (isSelectedOption) {
      const optionValue = String(answer.recipe.value ?? "").trim();
      const isYesNoOption = isYesNoValue(optionValue);
      if (isYesNoOption && parentFollowUpValues) {
        for (const subAnswer of subAnswers) {
          parentFollowUpValues.push(...collectSpecificValues(subAnswer, answersSelectedMap));
        }
        return;
      }
      const isSpecificOption = !!optionValue && !isYesNoOption;
      const followUpValues = isSpecificOption ? [] : parentFollowUpValues;
      const valueCountBefore = specificValues.length;
      for (const subAnswer of subAnswers) collectFromAnswer(subAnswer, followUpValues);
      const foundMoreSpecificValue = specificValues.length > valueCountBefore;
      if (isSpecificOption && !foundMoreSpecificValue) {
        specificValues.push(followUpValues?.length ? `${optionValue} (${followUpValues.join(", ")})` : optionValue);
      }
      return;
    }
    if (!isOption) {
      for (const subAnswer of subAnswers) collectFromAnswer(subAnswer, parentFollowUpValues);
    }
  };
  collectFromAnswer(answerNode, null);
  return specificValues;
}

/**
 * Collects every question flagged with show_in_health_summary that has a
 * selected answer. Value extraction walks the answer tree up to 2 levels
 * deep to handle plain checklists/radios, gate+list (current-treatment
 * style), and gate+free-text (other-health-factors style) without any
 * per-question special-casing.
 */
export function getHealthSummaryEntries(
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  language?: string
): HealthSummaryEntry[] {
  return flattenedQuestionnaire
    .filter((q: any) => q?.show_in_health_summary)
    .flatMap((q: any) => {
      const rootChildren = q.answersGroup?.children || [];

      function collectSelected(list: any[]): string | null {
        const values = list
          .filter((c: any) => answersSelectedMap[c.recipe.id])
          .map((c: any) => {
            if (isExclusiveNoneOption(c)) {
              return t(language, "health_summary_none_short") || "None";
            }
            const specific = collectSpecificValues(c, answersSelectedMap);
            if (specific.length) return specific.map((v) => normalizeNoneOfTheAbove(v, language) ?? v).join(", ");
            const rawValue = findFilledFreeText(c, answersSelectedMap) || c.recipe.value;
            return normalizeNoneOfTheAbove(rawValue, language);
          })
          .filter(Boolean);
        return values.join(", ") || null;
      }

      function collectNestedFreeText(list: any[]): string | null {
        for (const c of list) {
          const found = findFilledFreeText(c, answersSelectedMap);
          if (found) return found;
        }
        return null;
      }

      // Level 1: direct selections on the root answer group
      const direct = collectSelected(rootChildren);
      if (direct) {
        return [{ key: q.recipe_question_id ?? q.id, label: stripHtml(q.text ?? ""), value: direct, questionId: q.id }];
      }

      // Level 2: walk each root child's nested answer groups
      for (const rootChild of rootChildren) {
        const nestedChildren = rootChild.children?.[0]?.children;
        if (!nestedChildren) continue;
        const nested = collectSelected(nestedChildren) || collectNestedFreeText(rootChild.children);
        if (nested) {
          return [{ key: q.recipe_question_id ?? q.id, label: stripHtml(q.text ?? ""), value: nested, questionId: q.id }];
        }
      }

      return [];
    });
}

// ---------- HL-12029: health profile edit modal ----------

/**
 * State of the "Gesundheitsprofil bearbeiten" modal for a draft DOB / height / weight.
 *   incomplete   → height or weight missing / out of range: no message, Save disabled
 *   underage     → under 18 (HL-11440 B1 rule): red message, Save disabled
 *   not-eligible → BMI below the comorbidity threshold (< 27): red, Save disabled
 *   conditional  → 27 ≤ BMI ≤ 29.9 without a comorbidity: orange, Save active, the
 *                  comorbidity question is asked again after Save
 *   eligible     → BMI above the minimum threshold (> 29.9), or 27–29.9 with a comorbidity:
 *                  green, Save active
 */
export type HealthProfileEditState = "incomplete" | "underage" | "not-eligible" | "conditional" | "eligible";

export const MIN_PATIENT_AGE = 18;

export function getHealthProfileEditState(params: {
  bmi: number | null;
  age: number | null;
  hasComorbidity: boolean;
  comorbidityThreshold: number;
  minimumThreshold: number;
}): HealthProfileEditState {
  const { bmi, age, hasComorbidity, comorbidityThreshold, minimumThreshold } = params;
  if (bmi === null) return "incomplete";
  if (age !== null && age < MIN_PATIENT_AGE) return "underage";
  if (bmi > minimumThreshold) return "eligible";
  if (bmi >= comorbidityThreshold) return hasComorbidity ? "eligible" : "conditional";
  return "not-eligible";
}

/** Only these states let the user keep the edited values (ticket Part 1). */
export const isHealthProfileSavable = (state: HealthProfileEditState): boolean =>
  state === "eligible" || state === "conditional";

/** Parse a typed measurement; null when empty or not a number. */
export function parseMeasurement(raw: string): number | null {
  const trimmed = raw.trim().replace(",", ".");
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** True when the value is inside the recipe validation_rule range (unbounded sides pass). */
export function isWithinRule(value: number | null, rule: { min_value?: number; max_value?: number } | null | undefined): boolean {
  if (value === null) return false;
  if (rule?.min_value !== undefined && rule.min_value !== null && value < Number(rule.min_value)) return false;
  if (rule?.max_value !== undefined && rule.max_value !== null && value > Number(rule.max_value)) return false;
  return true;
}
